import {StyleSheet} from "aphrodite";
import React, {useEffect, useState} from "react";

import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import type {PropsFor, StyleType} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {useAIGuideToolPersona} from "@khan/guide-core-helpers";

import type {AIContentTabTool} from "#class-package/content/ai-content-tab-tool-cards-helpers.ts";
import {PARENT_KHANMIGO_TOOLS_PATH} from "#page-package/util.ts";

import AIToolIcon from "./ai-tool-icon.tsx";
import {ToolCategories, type Tool} from "./ai-tools-list.ts";
import FavoriteButton from "./favorite-button.tsx";
import {
    ADMIN_KHANMIGO_TOOLS_PATH,
    TEACHER_KHANMIGO_TOOLS_PATH,
} from "./path-helpers.ts";
import type {Platform} from "./utils.ts";

type DetailCellProps = PropsFor<typeof DetailCell>;

type AIToolCardProps = {
    tool: Tool | AIContentTabTool;
    style?: StyleType;
    rootStyle?: StyleType;
    horizontalRule?: DetailCellProps["horizontalRule"];
    disabled?: boolean;
    onClick?: (tool: Tool | AIContentTabTool) => void;
    isExtension?: boolean;
    onExtensionClick?: () => void;
    platform?: Platform;
    updateFullToolList?: (configName: string, isFavorited: boolean) => void;
    showFavoriteButton?: boolean;
    classCode?: string;
    isPNGIcon?: boolean;
};

const AIToolCard = ({
    tool,
    style,
    rootStyle,
    horizontalRule,
    disabled,
    onClick,
    isExtension,
    onExtensionClick,
    platform,
    updateFullToolList,
    showFavoriteButton = false,
    classCode,
    isPNGIcon,
}: AIToolCardProps): React.ReactElement => {
    const [isFavorited, setIsFavorited] = useState(false);
    const persona = useAIGuideToolPersona();

    // Ensure the favorited state is in sync with the tool's categories
    useEffect(() => {
        setIsFavorited(tool.categories.includes(ToolCategories.Favorites));
    }, [isFavorited, tool]);

    const handleDetailCellClick = () => {
        if (isExtension && onExtensionClick) {
            onExtensionClick();
        } else if (onClick) {
            onClick(tool);
        }
    };

    const isWritingCoach = tool.configName === "writing-coach-getting-started";
    const khanmigoToolPath =
        persona === "ADMIN"
            ? ADMIN_KHANMIGO_TOOLS_PATH
            : persona === "PARENT"
              ? PARENT_KHANMIGO_TOOLS_PATH
              : TEACHER_KHANMIGO_TOOLS_PATH; // default to teacher because there are more usages that assume the card will point to the teacher version.
    let href = `${khanmigoToolPath}/${tool.urlSlug}`;
    if (platform) {
        href += `?platform=${platform}`;
    }
    if (classCode && "contentID" in tool) {
        href = `/teacher/class/${classCode}/edit-your-assignment/${tool.contentID}`;
        if (isWritingCoach) {
            href = "/writing-coach/assignment/create";
        }
    } else if (isExtension) {
        href = "#";
    }

    return (
        <DetailCell
            href={href}
            target={isWritingCoach ? "_blank" : undefined}
            title={<LabelLarge>{tool.title}</LabelLarge>}
            subtitle2={
                <LabelXSmall style={styles.description}>
                    {tool.description}
                </LabelXSmall>
            }
            contentStyle={{
                alignSelf: "flex-start",
            }}
            leftAccessory={
                <AIToolIcon
                    icon={tool.icon}
                    color={tool.color}
                    isPNGIcon={isPNGIcon}
                />
            }
            rightAccessory={
                showFavoriteButton && (
                    <FavoriteButton
                        setIsFavorited={setIsFavorited}
                        isFavorited={isFavorited}
                        configName={tool.configName}
                        updateFullToolList={updateFullToolList}
                    />
                )
            }
            rightAccessoryStyle={{alignSelf: "flex-start"}}
            leftAccessoryStyle={{
                alignSelf: "flex-start",
            }}
            style={style}
            rootStyle={[styles.rootStyle, rootStyle]}
            horizontalRule={horizontalRule ?? "none"}
            disabled={disabled}
            data-testid="ai-tool-card"
            onClick={handleDetailCellClick}
            role="button"
            testId="ai-tool-card"
        />
    );
};

const styles = StyleSheet.create({
    description: {
        color: color.offBlack64,
    },
    rootStyle: {
        borderRadius: spacing.xSmall_8,
    },
});

export default AIToolCard;
